const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/middleware/kisapi.ts';
/**
 * KisApi redux middleware
 *
 * this module intercepts actions with a certain object
 * signature and handles network traffic with kisim backend
 */
import _ from 'lodash';
import { Action, Store } from 'redux';
import IntlMessageFormat from 'intl-messageformat';
import { pushLog } from '../actions/logActions';
import { showSnackbar, showUncaughtErrorDialog } from '../actions/uiActions';
import { REVOKE_TOKEN } from '../actions/userActions';
import createErrorMessage from './kisapi/errors';
import { GenericRequest, IAsyncMiddleware } from './genericMiddleware';
import { DEFAULT_LOCALE, intlLanguagesMap } from '../utils/intlUtils';

export type ActionWithMeta = Action & { meta: any };

// Action key that carries API call info interpreted by this middleware.
// NOTE: We might like to use ES6 Symbols to generate unique key here, but for
// now jasmine testing makes problems (for prototype stay with string)
export const KISAPI = 'KISAPI'; // Symbol('Call KISAPI');

export interface IKisApiRequest extends GenericRequest {
    needsNoAuthentication?: boolean;
}

// this is just for documentation purposes on which meta fields would be
// evaluated by this middleware. Any other fields will just be piped through
// interface IKisApiMetaInfo {
//    suppressErrorMsg: boolean;
//    overrideErrorMsg: ReactIntl.FormatMessage.MessageDescriptor
// }

// TODO: get a working type signature for the curried middleware functions

export interface IKisApiConfig {
    /**
     * define base url of api backend e.g. http://whatever.com
     * This may be null, in this case, kisapi module 'balancer.ts' should be included in the pipeline
     */
    baseUrl?: string | string[];

    /**
     * define http request config to overwrite built in defaults for all requests
     */
    httpConfig?: RequestInit;
    /**
     * configure an array of transformation function that the http request will be piped through
     */
    pipeline: IAsyncMiddleware[];
}

// parse response according to content-type header
export function parseBody(response: Response): Promise<any> {
    // an error type response doesn't contain any headers, but we want to make sure
    // to return an empty json object
    if (response.type === 'error') {
        return Promise.resolve({});
    }

    const contentType = response.headers.get('content-type')?.split?.(';');
    if (contentType && contentType.includes('application/json')) {
        return response.json();
    } else if (contentType && contentType.includes('application/pdf')) {
        return response.arrayBuffer();
    } else {
        return response.text();
    }
}

/**
 * Redux middleware for handling KISAPI actions
 */
export default function kisApiMiddleware(config: IKisApiConfig) {
    const defaultRequest: RequestInit = {
        method: 'GET',
        headers: {
            accept: 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
    };
    let replayQueue: any[] = []; // Array<KisApiActions>
    return (store: Store) => (next: any) => async (action: any) => {
        // assert correct format of action object
        if (typeof action[KISAPI] === 'undefined') {
            return next(action);
        }

        if (_.isEmpty(config.pipeline)) {
            throw new Error('KisApi middleware is not configured correctly.');
        }

        let pending: string;
        let success: string;
        let failure: string;

        if (typeof action.type === 'string') {
            const typeArr = action.type.split(',');
            if (typeArr.length !== 3) {
                throw new Error(`String-based KISAPI action type has unexpected length of ${typeArr.length}`);
            } else {
                pending = typeArr[0];
                success = typeArr[1];
                failure = typeArr[2];

                if (process.env.NODE_ENV !== 'production') {
                    // Extra check is only relevant during development and skipped in production for better performance
                    if (pending !== pending.trim() || success !== success.trim() || failure !== failure.trim()) {
                        throw new Error(`String-based KISAPI action types must be properly trimmed`);
                    }
                }
            }
        } else if (Array.isArray(action.type)) {
            throw new Error(`Array-based KISAPI action type is no longer supported: ${JSON.stringify(action.type)}`);
        } else {
            throw new Error(`KISAPI action has unexpected type ${action.type} (${typeof action.type})`);
        }

        if (!pending || !success || !failure) {
            throw new Error(`KISAPI action is missing at least one action type: ${JSON.stringify(action.type)}`);
        }

        Object.assign(defaultRequest, config.httpConfig);
        const chain = _.flowRight(config.pipeline.map((item: any) => item(store)))();

        const kisApiCall = Object.assign({}, action[KISAPI]);

        kisApiCall.endpoint =
            (kisApiCall.baseUrl || (Array.isArray(config.baseUrl) ? config.baseUrl[0] : config.baseUrl) || '') +
            kisApiCall.endpoint;

        kisApiCall.requestConfig = Object.assign({}, defaultRequest, kisApiCall.requestConfig);

        // callout pending action only if the action is not tagged to been replayed

        if (!action.meta || (action.meta && !action.meta.replay)) {
            Promise.resolve().then(() =>
                next({
                    type: pending,
                    meta: action.meta || {},
                })
            );
        }
        const response = await chain(kisApiCall);

        // handle 401 error here again by retrying the failed requests a few times or potentially keep a queue here
        if (response.status === 401) {
            // current ad-hoc (hacky) version to suppress additional pending action after replaying queue
            // be careful that action obj is not mutated above so the replay will be able to trigger the original action
            replayQueue.push(_.merge(action, { meta: { replay: true } }));
        }

        // delete all pending action in the queue if we need to suspect the user logged out while waiting on
        // a refresh token
        if (success === REVOKE_TOKEN.success && response.status === 200) {
            replayQueue = [];
        }

        // probably too naive approach (might have race conditions) but try to keep is simple for now
        // flush the replayQueue as soon as a 200 code (potentially coming from re-authentication or refreshing) passes
        // by
        if (response.status === 200 && !_.isEmpty(replayQueue)) {
            // flush queue and reset it to empty
            // wait for one tick to give redux a chance to update the new access_token in the reducer
            // worst case two 401 requests and two refresh requests could happen but that doesn't change functionality
            setTimeout(() => {
                replayQueue.forEach(store.dispatch);
                replayQueue = [];
            }, 1);
        }

        const wasSuccessful = response.status < 400 && response.type !== 'error';
        // error handling and matching of accept headers and transformation function
        // needs to get much more sophisticated
        let content: any;
        try {
            // TODO: the response parser still needs to be used according to accept header settings
            // and handle at least text, json and binary blobs correctly
            content = await parseBody(response);
        } catch (e) {
            // suppress error log if call has failed; in most cases the content is invalid in these cases, spamming the console
            if (wasSuccessful) {
                // probably no valid json in response body
                console.log(e);
            }
            content = {};
        }
        // form regular action that can be consumed by reducer
        // content.error_description might no be available => TODO: include more checks
        // assert that action.meta is either undefined or an object.
        const actionMeta = action.meta || {};
        if (typeof actionMeta !== 'object') {
            throw new Error('meta entry for kisapi middleware needs to be an object or undefined');
        }
        const online: boolean = store.getState().network.get('online');

        if (!wasSuccessful && !actionMeta.suppressErrorMsg && response.status !== 401 && online) {
            // transform non-object contents into an object to eliminate crashes caused by sloppy error messages sent by KISAPI
            if (typeof content !== 'object') {
                content = {
                    Errors: [{ Msg: content }],
                };
            }
            // TODO: make error handling more fine grained. API mostly doesn't give user friendly error messages
            // content.error_description = content.error_description || 'Ein Netzwerkfehler ist aufgetreten';

            // if Error array is returned take first message as error description
            // this might not be the ideal solution, but better than nothing for now.
            if (actionMeta.overrideErrorMsg) {
                const locale = store.getState().i18n.get('locale');
                const map = intlLanguagesMap[locale] || intlLanguagesMap[DEFAULT_LOCALE];
                const msg = new IntlMessageFormat(map[actionMeta.overrideErrorMsg.id], locale);
                content.error_description = msg.format();
            } else {
                const arrayVariant = content.Errors && content.Errors[0] && content.Errors[0].Msg;
                const qstructVariant = content.Errors && content.Errors['1'] && content.Errors['1'].Msg;
                content.error_description = arrayVariant || qstructVariant;
            }

            const msg = content.error_description
                ? { id: 'NET::ERR', defaultMessage: content.error_description }
                : createErrorMessage(response);
            next(showSnackbar(msg));

            store.dispatch(
                pushLog('warning', `KISAPI status ${response.status}`, {
                    location: 'kisapi.ts',
                    status: response.status,
                    actionType: action.type,
                    errorDescription: content.error_description,
                })
            );
        }

        const hasMore = typeof content === 'object' && content?.Info?.HasMoreResultRows === '1';

        if (wasSuccessful && hasMore) {
            // KISAPI sent us back a flag indicating that the returned result set is incomplete,
            // because the number of entries exceeded the MaxResultRows parameter.

            if (actionMeta.showDlgWhenResultHasMoreRows) {
                next(showUncaughtErrorDialog(null, null, null, 'kisapiResultHasMoreRows'));
            }
        }

        if (_.isEmpty(content.error_description) && !wasSuccessful) {
            const errMessage = createErrorMessage(response);
            const locale = store.getState().i18n.get('locale');
            const map = intlLanguagesMap[locale] || intlLanguagesMap[DEFAULT_LOCALE];
            const msg = new IntlMessageFormat(map[errMessage.id!!], locale);
            // if error message comes as html from webserver instead of kisserver.dll
            if (response.headers.get('content-type') !== 'application/json') {
                content = { returnedValue: content };
            }
            content.error_description = msg.format();
        }
        const headersMeta = response.headers.get('ETag') ? { etag: response.headers.get('ETag') } : {};
        if (response.status !== 401) {
            Promise.resolve().then(() =>
                next({
                    type: wasSuccessful ? success : failure,
                    payload: content,
                    meta: Object.assign({}, _.omit(actionMeta, 'replay'), headersMeta, {
                        responseStatus: response.status,
                    }),
                })
            );
        }
    };
}
